import type { FunctionComponent } from 'react';
import { lazy, Suspense, useMemo } from 'react';

import type { DialogContentProps } from './DialogContent';
import type { ProductInfoDialogProps } from './types';
import { Dialog } from '../components/Dialog';
import { Loading } from '../components/Loading';
import { DIALOG_MODE_LOADING } from '../constants';
import { useIsDesktop } from '../hooks/internal';
import { useTranslations } from '../hooks/useTranslations';
import { getInitialProduct } from '../utils';

export const DialogContent = lazy<FunctionComponent<DialogContentProps>>(async () => ({
  default: (await import('./DialogContent')).DialogContent,
}));

export const DialogContainer = ({
  analytics = {},
  customer = {},
  dialog = {},
  handlers = {},
  initialProductCode,
  mode = DIALOG_MODE_LOADING,
  products = [],
  shared = {},
  replaceSubscription,
}: ProductInfoDialogProps) => {
  const { originalItemCode } = shared;
  const { shade } = getInitialProduct({ initialProductCode, products });
  const { canBeReserved } = shade ?? {};
  const { translations } = useTranslations();
  const isDesktop = useIsDesktop();
  const header = useMemo(() => {
    if (!isDesktop) {
      if (originalItemCode) {
        return translations.replaceWith;
      }
      if (canBeReserved) {
        return translations.reserve;
      }
    }

    return undefined;
  }, [canBeReserved, isDesktop, originalItemCode, translations.replaceWith, translations.reserve]);

  return (
    <Dialog
      header={header}
      {...dialog}
    >
      <Suspense fallback={<Loading />}>
        {mode === DIALOG_MODE_LOADING ? (
          <Loading />
        ) : (
          <DialogContent
            analytics={analytics}
            customer={customer}
            handlers={handlers}
            initialProductCode={initialProductCode}
            mode={mode}
            products={products}
            shared={shared}
            replaceSubscription={replaceSubscription}
          />
        )}
      </Suspense>
    </Dialog>
  );
};
